import { useEffect } from 'react';
import { Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useSpeechToText } from '@/hooks/useSpeechToText';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';

/**
 * Button that starts/stops speech-to-text and calls onResult with the transcript.
 * Uses browser Web Speech API for both desktop and mobile. On mobile: single-shot mode.
 * When unsupported (e.g. Firefox), shows disabled button with tooltip.
 */
export default function SpeechToTextButton({
  onResult,
  currentValue = '',
  append = true,
  disabled = false,
  className,
  size = 'sm'
}) {
  const isMobile = useIsMobile(768);

  const browser = useSpeechToText({
    lang: 'pl-PL',
    onResult,
    append,
    currentValue,
    singleShot: isMobile,
  });

  useEffect(() => {
    if (browser.error) toast.error(browser.error);
  }, [browser.error]);

  const handleClick = () => {
    if (!browser.isSupported) return;
    if (browser.isListening) {
      browser.stopListening();
    } else {
      browser.startListening();
    }
  };

  const label = browser.isListening ? 'Zatrzymaj (Nagrywanie…)' : 'Wpisz głosowo';
  const unsupportedLabel = 'Wpisywanie głosowe nie jest obsługiwane w tej przeglądarce. Spróbuj Chrome lub Edge.';

  const button = (
    <Button
      type="button"
      variant="ghost"
      size={size === 'sm' ? 'sm' : size === 'icon' ? 'icon' : 'default'}
      className={cn(
        'shrink-0',
        browser.isListening && 'text-red-600 hover:text-red-700',
        className
      )}
      disabled={!browser.isSupported || disabled}
      onClick={handleClick}
      aria-label={label}
      title={browser.error || (browser.isSupported ? label : unsupportedLabel)}
    >
      <Mic className={cn('size-4', size === 'lg' && 'size-5')} />
      {browser.isListening && size !== 'icon' && (
        <span className="ml-1 text-xs font-normal">Nagrywanie…</span>
      )}
    </Button>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="top">
          {browser.error || (browser.isSupported ? label : unsupportedLabel)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
